import { getDefaultCountryCode } from 'intl/helpers';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { ValidationTranslationKeys } from 'intl/keys';
import {
  matchIsValidTel,
  MuiTelInput,
  MuiTelInputCountry,
  MuiTelInputInfo,
} from 'mui-tel-input';
import { forwardRef, memo, ReactElement, Ref, useState } from 'react';
import { useIntl } from 'react-intl';
import { getHelperTextFromError } from './common';
import { DisabledPhoneInputProps, PhoneInputProps } from './types';
import { Tooltip } from '@mui/material';

function DisabledPhoneInput(
  { value, localisedLabel, dense }: DisabledPhoneInputProps<string>,
  ref: Ref<HTMLDivElement>
): ReactElement {
  return (
    <MuiTelInput
      fullWidth
      value={value}
      disabled={true}
      data-qa='user-view-phone-input'
      label={localisedLabel}
      defaultCountry={getDefaultCountryCode() as MuiTelInputCountry}
      margin={dense ? 'dense' : 'none'}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 1.5,
        },
      }}
      InputProps={{ style: { paddingLeft: 0 } }}
    />
  );
}

function PhoneInput(
  {
    value,
    localisedLabel,
    handleChange,
    error,
    dense,
    reserveSpaceForHelperText,
    disabled,
  }: PhoneInputProps<string>,
  ref: Ref<HTMLDivElement>
): ReactElement {
  const intl = useIntl();
  const [_error, _setError] = useState<string | undefined>(error);
  const handlePhoneChange = (
    newValue: string,
    _: MuiTelInputInfo
  ): void => {
    const error = validatePhone(newValue);

    _setError(error);
    // because stateful component expects error on the first place
    handleChange({}, newValue, _);
  };
  const validatePhone = (value: string): string => {
    if (value && !matchIsValidTel(value)) {
      return formatTranslation(intl, ValidationTranslationKeys.invalidPhone);
    }

    return '';
  };

  return (
    <Tooltip
      title={
        typeof disabled === 'object'
          ? formatTranslation(intl, unsafeTranslationKey(disabled.reason) )
          : undefined
      }
      sx={{
        mt: 0,
      }}
    >
      <MuiTelInput
        fullWidth
        value={value}
        onChange={handlePhoneChange}
        data-qa='user-view-phone-input'
        label={localisedLabel}
        error={!!_error}
        disabled={!!disabled}
        helperText={
          getHelperTextFromError(_error, intl) ||
          (reserveSpaceForHelperText && ' ')
        }
        defaultCountry={getDefaultCountryCode() as MuiTelInputCountry}
        margin={dense ? 'dense' : 'none'}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
          },
          '&.MuiFormControl-root': {
            marginTop: '0',
          },
        }}
      />
    </Tooltip>
  );
}
export const MemoPhoneInput = memo(forwardRef(PhoneInput));
export const MemoDisabledPhoneField = memo(forwardRef(DisabledPhoneInput));
